import styled from 'styled-components';
import { Dialog, FlexColumn } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import useTestNotificationsSettings from 'testManagement/hooks/useTestNotificationsSettings';
import { ReactComponent as NotificationsMuteIcon } from '@elementary/common-ui/assets/20x20/notifications-mute.svg';
import { ReactComponent as NotificationsIcon } from '@elementary/common-ui/assets/20x20/notifications.svg';
import { componentToken, fontWeight } from '@elementary/common-ui/style/utils';

const Text = styled(Typography)`
  font-size: ${componentToken('dialog.message.fontSize')};
`;

type Props = {
  open: boolean;
  onClose: () => void;
  mode: 'mute' | 'unmute' | '';
  selectedTestsIds: string[];
};

const ChangeNotificationsDialog = ({
  open,
  onClose,
  mode,
  selectedTestsIds,
}: Props) => {
  const itemsCount = selectedTestsIds?.length;

  const { unMuteTests, muteTests, isMuteTestsLoading, isUnMuteTestsLoading } =
    useTestNotificationsSettings();

  const handleConfirm = async () => {
    if (mode === 'mute') await muteTests(selectedTestsIds);
    else await unMuteTests(selectedTestsIds);
    onClose();
  };

  const title = `${
    mode === 'mute' ? 'Disable' : 'Enable'
  } Alerts for ${itemsCount} Tests`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      titleIcon={
        mode === 'unmute' ? <NotificationsIcon /> : <NotificationsMuteIcon />
      }
      title={title}
      cancelText="Cancel"
      confirmText="Apply"
      onConfirmButtonClick={handleConfirm}
      isLoading={isMuteTestsLoading || isUnMuteTestsLoading}
      message={
        <Text>
          Are you sure you want to{' '}
          <Text sx={{ display: 'inline', fontWeight: fontWeight('semibold') }}>
            turn {mode === 'unmute' ? 'on' : 'off'} alerts
          </Text>{' '}
          for the selected tests?
        </Text>
      }
      secondaryMessage={
        <FlexColumn sx={{ gap: 0.5 }}>
          <Text sx={{ color: 'content.tertiary' }}>
            {mode === 'unmute'
              ? 'Once enabled, alerts will be sent for any issues with these tests that match existing alert destinations.'
              : 'While alerts are disabled, no alerts will be sent for any issues related to these tests.'}
          </Text>
          <Text sx={{ color: 'content.tertiary' }}>
            You can {mode === 'unmute' ? 'disable' : 're-enable'} alerts at any
            time from this screen.
          </Text>
        </FlexColumn>
      }
    />
  );
};

export default ChangeNotificationsDialog;
